/**
 * Read the absolute expiry (epoch ms) encoded in a presigned S3 / CloudFront URL,
 * so the interview view can re-mint the resume URL before it lapses instead of
 * letting the browser hit an expired URL (S3 403 → "unable to load"). Returns
 * `null` when the URL carries no parseable expiry.
 */
export const getPresignedUrlExpiry = (url?: string | null): number | null => {
  if (!url) return null;

  let params: URLSearchParams;
  try {
    params = new URL(url).searchParams;
  } catch {
    const q = url.indexOf('?');
    if (q === -1) return null;
    params = new URLSearchParams(url.slice(q + 1));
  }

  // SigV2 / CloudFront canned policy: `Expires` is an absolute epoch (seconds).
  const expires = params.get('Expires');
  if (expires && /^\d+$/.test(expires)) return Number(expires) * 1000;

  // SigV4 (Rails/ActiveStorage default): absolute expiry =
  // X-Amz-Date (signing time) + X-Amz-Expires (seconds).
  const amzDate = params.get('X-Amz-Date');
  const amzExpires = params.get('X-Amz-Expires');
  if (amzDate && amzExpires && /^\d+$/.test(amzExpires)) {
    const signedAt = parseAmzDate(amzDate);
    if (signedAt !== null) return signedAt + Number(amzExpires) * 1000;
  }

  return null;
};

// X-Amz-Date is ISO-8601 basic format: `YYYYMMDDTHHMMSSZ`.
const parseAmzDate = (value: string): number | null => {
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(value);
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m;
  return Date.UTC(+y, +mo - 1, +d, +h, +mi, +s);
};
